import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  birdsService,
  birdBatchesService,
  birdProcurementsService,
  birdBlocksService,
} from '../services/apiServices';
import { queryKeys } from '../config/queryClient';
import { Bird, BirdProcurement, BirdLog, PaginatedResponse, Stats, BirdStats } from '../types';

// ============== Birds ==============

// Get all birds
export const useBirds = (params?: any) => {
  return useQuery<PaginatedResponse<Bird>>({
    queryKey: queryKeys.birdsList(params),
    queryFn: () => birdsService.getAll(params),
  });
};

export const useBirdTypes = () => {
  return useQuery<string[]>({
    queryKey: queryKeys.birdTypes,
    queryFn: birdsService.getTypes,
  });
};

// Get single bird
export const useBird = (id: number, enabled = true) => {
  return useQuery<Bird>({
    queryKey: queryKeys.bird(id),
    queryFn: () => birdsService.getById(id),
    enabled,
  });
};

// Get birds stats
export const useBirdsStats = () => {
  return useQuery<BirdStats>({
    queryKey: queryKeys.birdsStats,
    queryFn: birdsService.getStats,
  });
};

// Get birds quantities
export const useBirdsQuantities = () => {
  return useQuery({
    queryKey: queryKeys.birdsQuantities,
    queryFn: birdsService.getQuantities,
  });
};

// Get bird logs
export const useBirdLogs = (id: number, enabled = true) => {
  return useQuery<BirdLog[]>({
    queryKey: queryKeys.birdLogs(id),
    queryFn: () => birdsService.getLogs(id),
    enabled,
  });
};

// Create bird
export const useCreateBird = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Bird>) => birdsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

// Update bird
export const useUpdateBird = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Bird> }) =>
      birdsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bird(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdsStats });
    },
  });
};

// Delete bird
export const useDeleteBird = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => birdsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

// Start laying
export const useStartLaying = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => birdsService.startLaying(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bird(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdsStats });
    },
  });
};

export const useFeedEstimate = (id: number) => {
  return useQuery({
    queryKey: ['feedEstimate', id],
    queryFn: () => birdsService.calculateFeedEstimate(id),
    enabled: !!id,
  });
};

export const useUpdateFeedInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => birdsService.updateFeedInfo(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bird(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
    },
  });
};

// ============== Bird Blocks ==============

export const useBirdBlocks = (birdId: number) =>
  useQuery({
    queryKey: queryKeys.birdBlocks(birdId),
    queryFn: () => birdBlocksService.getAll(birdId),
    enabled: !!birdId,
  });

export const useBirdBlocksFeed = (birdId: number, enabled = true) =>
  useQuery({
    queryKey: queryKeys.birdBlocksFeed(birdId),
    queryFn: () => birdBlocksService.feedAll(birdId),
    enabled: !!birdId && enabled,
  });

export const useCreateBirdBlocks = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { blocks: any[] }) => birdBlocksService.create(birdId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.birdBlocks(birdId) });
      qc.invalidateQueries({ queryKey: queryKeys.bird(birdId) });
    },
  });
};

export const useUpdateBirdBlock = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, data }: { blockId: number; data: any }) =>
      birdBlocksService.update(birdId, blockId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.birdBlocks(birdId) });
      qc.invalidateQueries({ queryKey: queryKeys.birdBlocksFeed(birdId) });
    },
  });
};

export const useDeleteBirdBlock = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockId: number) => birdBlocksService.delete(birdId, blockId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.birdBlocks(birdId) });
      qc.invalidateQueries({ queryKey: queryKeys.birdBlocksFeed(birdId) });
    },
  });
};

// ============== Bird Batches ==============

export const useBirdBatches = (params?: any) =>
  useQuery({
    queryKey: queryKeys.birdBatchesList(params),
    queryFn: () => birdBatchesService.getAll(params),
  });

export const useBirdBatch = (id: number | undefined) =>
  useQuery({
    queryKey: queryKeys.birdBatch(id ?? 0),
    queryFn: () => birdBatchesService.getById(id ?? 0),
    enabled: !!id,
  });

export const useUpdateBirdBatch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => birdBatchesService.update(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.birdBatches });
      qc.invalidateQueries({ queryKey: queryKeys.birdBatch(id) });
    },
  });
};

// ============== Bird Procurements ==============

// Get all bird procurements
export const useBirdProcurements = (params?: any) => {
  return useQuery<PaginatedResponse<BirdProcurement>>({
    queryKey: queryKeys.birdProcurementsList(params),
    queryFn: () => birdProcurementsService.getAll(params),
  });
};

// Get all suppliers
export const useSuppliers = () => {
  return useQuery<any[]>({
    queryKey: queryKeys.birdProcurementsSuppliers,
    queryFn: () => birdProcurementsService.getSuppliers(),
  });
};

// Get single bird procurement
export const useBirdProcurement = (id: number, enabled = true) => {
  return useQuery<BirdProcurement>({
    queryKey: queryKeys.birdProcurement(id),
    queryFn: () => birdProcurementsService.getById(id),
    enabled,
  });
};

// Get bird procurements stats
export const useBirdProcurementsStats = () => {
  return useQuery<Stats>({
    queryKey: queryKeys.birdProcurementsStats,
    queryFn: birdProcurementsService.getStats,
  });
};

// Create bird procurement
export const useCreateBirdProcurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<BirdProcurement>) => birdProcurementsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurementsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

// Update bird procurement
export const useUpdateBirdProcurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BirdProcurement> }) =>
      birdProcurementsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurement(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurements });
    },
  });
};

// Delete bird procurement
export const useDeleteBirdProcurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => birdProcurementsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurementsStats });
    },
  });
};

// Mark bird procurement as delivered
export const useMarkBirdProcurementAsDelivered = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      birds,
    }: {
      id: number;
      birds: { bird_id: number; quantity_delivered: number; date_delivered: string }[];
    }) => birdProcurementsService.markAsDelivered(id, { birds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.birdProcurementsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};
